"use client";

import Image from "next/image";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Building2, Clock, Ticket, Users, BarChart3, Shield, Zap } from "lucide-react";

export default function Loading() {
  const features = [
    {
      icon: Ticket,
      title: "Ticket Management",
      description: "Efficiently track and manage support requests",
    },
    {
      icon: Clock,
      title: "Time Tracking",
      description: "Accurate time logging and billing",
    },
    {
      icon: Users,
      title: "Contact Portal",
      description: "Secure access for client collaboration",
    },
    {
      icon: BarChart3,
      title: "Reporting",
      description: "Comprehensive analytics and insights",
    },
    {
      icon: Shield,
      title: "Security",
      description: "Enterprise-grade security and compliance",
    },
    {
      icon: Zap,
      title: "Performance",
      description: "Fast and reliable system performance",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <Image
                src="/logo_long.svg"
                alt="West Tennessee Consulting Hub"
                width={200}
                height={64}
                className="h-16 w-auto mx-auto"
              />
            </div>
            <CardTitle className="text-3xl font-bold text-card-foreground">WTC Hub</CardTitle>
            <CardDescription className="text-lg text-gray-600 dark:text-gray-400">
              Loading your workspace...
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-8">
            {/* Loading Animation */}
            <div className="flex justify-center">
              <div className="text-center">
                <LoadingSpinner size="xl" />
                <p className="text-sm text-gray-500 mt-4">Initializing system components</p>
              </div>
            </div>

            {/* Progress Indicators */}
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Authentication</span>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-green-600 dark:text-green-400">Complete</span>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Database Connection</span>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <span className="text-blue-600 dark:text-blue-400">Connecting...</span>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">User Interface</span>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-card-400 rounded-full"></div>
                  <span className="text-gray-400">Pending</span>
                </div>
              </div>
            </div>

            {/* System Features */}
            <div>
              <h3 className="text-lg font-semibold text-card-foreground mb-4 text-center">
                System Features
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div key={index} className="p-4 bg-card-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
                          <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <h4 className="font-medium text-card-foreground text-sm">
                            {feature.title}
                          </h4>
                          <p className="text-xs text-gray-600 dark:text-gray-400">{feature.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Loading Tips */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-card-foreground mb-4 text-center">
                While you wait...
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-950/40 rounded-lg">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Ensure you have a stable internet connection
                  </p>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-950/40 rounded-lg">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    The system will automatically load your dashboard
                  </p>
                </div>
                <div className="flex items-center gap-3 p-3 bg-purple-50 dark:bg-purple-950/40 rounded-lg">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <p className="text-sm text-purple-700 dark:text-purple-300">
                    Your session will be securely maintained
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="text-center pt-4 border-t">
              <p className="text-sm text-gray-500 mb-2">Taking longer than expected?</p>
              <div className="flex justify-center gap-4">
                <a
                  href="mailto:support@wtc.com"
                  className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Contact Support
                </a>
                <span className="text-sm text-gray-400">|</span>
                <a href="tel:+1-555-0123" className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                  Call Us
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
